'use client';

import { APP_SCORING_PERCENTAGE_RANGES, FEATURE_FLAGS } from '@/constants';
import {
  ApiRoundedRatingCutoffLevel,
  ApiRoundedRatingCutoffs,
  TNormalizedRatingCutoffs,
  TRatingCutoffBand,
} from '@/types';

// ================================
// Internal Constants
// ================================

const REQUIRED_LEVELS: ApiRoundedRatingCutoffLevel[] = [
  'L1',
  'L2',
  'L3',
  'L4',
];

/**
 * Pre-computed fallback cutoffs derived from the static app constant.
 *
 * These are used whenever the API payload is null, missing required
 * levels, or contains invalid numeric bounds.
 */
export const FALLBACK_RATING_CUTOFFS: TNormalizedRatingCutoffs = {
  L1: APP_SCORING_PERCENTAGE_RANGES.L1 as TRatingCutoffBand,
  L2: APP_SCORING_PERCENTAGE_RANGES.L2 as TRatingCutoffBand,
  L3: APP_SCORING_PERCENTAGE_RANGES.L3 as TRatingCutoffBand,
  L4: APP_SCORING_PERCENTAGE_RANGES.L4 as TRatingCutoffBand,
};

// ================================
// Internal Helpers
// ================================

/**
 * Returns true when `band` contains valid inclusive numeric bounds.
 *
 * Validity rules:
 * - Both values must be finite numbers.
 * - Both values must be non-negative.
 * - `low` must not exceed `high`.
 */
function isValidBand(band: TRatingCutoffBand): boolean {
  const [low, high] = band;
  return (
    typeof low === 'number' &&
    typeof high === 'number' &&
    isFinite(low) &&
    isFinite(high) &&
    low >= 0 &&
    high >= 0 &&
    low <= high
  );
}

// ================================
// Public Domain Policy
// ================================

/**
 * Normalizes the raw `rounded_rating_cutoffs` API payload into a
 * `TNormalizedRatingCutoffs` map (`L1–L4 => [low, high]`).
 *
 * Validation rules:
 * - Payload must be a non-empty array.
 * - All four levels (L1–L4) must be present.
 * - Each band must have non-negative numeric bounds where `low <= high`.
 *
 * Returns `FALLBACK_RATING_CUTOFFS` (derived from
 * `APP_SCORING_PERCENTAGE_RANGES`) whenever the payload is null,
 * invalid, or incomplete.
 */
export function normalizeRatingCutoffs(
  payload: ApiRoundedRatingCutoffs,
): TNormalizedRatingCutoffs {
  // Short-circuit when feature flag is disabled to avoid unnecessary processing.
  if (!FEATURE_FLAGS.ENABLE_SCORECARD_V2_VARIABLE_THRESHOLDS) {
    return FALLBACK_RATING_CUTOFFS;
  }

  // Short-circuit when payload is null, not an array, or empty. This covers the
  // common case of missing cutoffs and guards against unexpected schema drift.
  if (!Array.isArray(payload) || payload.length === 0) {
    return FALLBACK_RATING_CUTOFFS;
  }

  // Merge all array entries into a single flat map keyed by level.
  const merged: Partial<Record<
    ApiRoundedRatingCutoffLevel,
    TRatingCutoffBand
  >> = {};

  for (const entry of payload) {
    for (const level of REQUIRED_LEVELS) {
      const range = entry[level];
      if (range !== undefined) {
        merged[level] = [range.low, range.high];
      }
    }
  }

  // Validate every required level is present with valid bounds.
  for (const level of REQUIRED_LEVELS) {
    const band = merged[level];
    if (!band || !isValidBand(band)) {
      return FALLBACK_RATING_CUTOFFS;
    }
  }

  return merged as TNormalizedRatingCutoffs;
}
